import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import {
  ArrowLeft,
  ChevronDown,
  Clapperboard,
  Clock,
  Languages,
  Loader2,
  Monitor,
  Scan,
  Sparkles,
  Type,
  Wand2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { appApiBaseUrl, appFetch } from '@/shared/api/app-client'
import type { ApiResponse, PaginatedResponse, Template } from '@/shared/api/app-types'
import { captureFirstFrame } from '@/features/app-shell/utils/cover-extractor'
import {
  TEMPLATE_BASE,
  captionLibraryStyles,
  CaptionStyleLibraryCard,
  type CaptionStyleOption,
} from './home-canvas'
import { cn } from '@/shared/ui/cn'

const videoGenres = [
  { value: 'Talking', label: '对话' },
  { value: 'Presentation', label: '演示' },
  { value: 'Gaming', label: '游戏' },
  { value: 'Cinema', label: '电影' },
]

const previewStyles: CaptionStyleOption[] = captionLibraryStyles.slice(0, 4)

const languages = [
  { value: 'zh', label: '中文' },
  { value: 'en', label: '英语' },
  { value: 'ja', label: '日语' },
  { value: 'ko', label: '韩语' },
  { value: 'es', label: '西班牙语' },
  { value: 'fr', label: '法语' },
  { value: 'de', label: '德语' },
  { value: 'pt', label: '葡萄牙语' },
  { value: 'hi', label: '印地语' },
  { value: 'ar', label: '阿拉伯语' },
]

const orientations = [
  { value: 'portrait', label: '竖屏 (9:16)' },
  { value: 'landscape', label: '横屏 (16:9)' },
  { value: 'square', label: '方形 (1:1)' },
]

const resolutions = [
  { value: '720', label: '720p' },
  { value: '1080', label: '1080p' },
  { value: '4k', label: '4K' },
]

const clipLengths = [
  { value: '<30s', label: '<30秒', maxSeconds: 30 },
  { value: '30s-60s', label: '30-60秒', maxSeconds: 60 },
  { value: '60s-90s', label: '60-90秒', maxSeconds: 90 },
  { value: '90s-3min', label: '90秒-3分钟', maxSeconds: 180 },
]

function SelectField({
  label,
  icon: Icon,
  value,
  onChange,
  options,
}: {
  label: string
  icon: typeof Languages
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
}) {
  return (
    <div className="space-y-2.5">
      <label className="flex items-center gap-2 text-sm font-medium text-foreground">
        <Icon className="size-4 text-muted-foreground" />
        {label}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-full appearance-none rounded-lg border border-border bg-card px-3 pr-8 text-sm text-foreground shadow-sm focus:outline-none focus:ring-1 focus:ring-primary"
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
      </div>
    </div>
  )
}

function PillButton({
  active,
  disabled = false,
  onClick,
  children,
}: {
  active: boolean
  disabled?: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors',
        disabled
          ? 'cursor-not-allowed border-border bg-muted text-muted-foreground/40'
          : active
            ? 'border-primary bg-primary/10 text-primary'
            : 'border-border bg-card text-muted-foreground hover:bg-accent',
      )}
    >
      {children}
    </button>
  )
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function extractFileName(url: string): string {
  const parts = url.split('/')
  const last = parts[parts.length - 1] ?? url
  return last.split('?')[0] ?? last
}

function resolveMediaUrl(url: string): string {
  if (url.startsWith('/uploads/')) {
    return `${appApiBaseUrl}${url}`
  }
  return url
}

function getTemplateCaptionStyleKey(config: Record<string, unknown> | null | undefined): string | null {
  if (!config || typeof config !== 'object' || Array.isArray(config)) {
    return null
  }
  const value = config.captionStyleKey
  return typeof value === 'string' && value.trim().length > 0 ? value : null
}

async function generateAndUploadCover(projectId: number, videoSrc: string): Promise<void> {
  const videoUrl = resolveMediaUrl(videoSrc)
  const blob = await captureFirstFrame(videoUrl)
  const file = new File([blob], `cover-${projectId}.webp`, { type: 'image/webp' })
  const formData = new FormData()
  formData.append('file', file)

  const res = await fetch(`${appApiBaseUrl}/api/projects/${projectId}/cover`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  })

  if (!res.ok) {
    throw new Error(`cover upload failed (${res.status})`)
  }
}

function BrandTemplatePicker({
  templates,
  loading,
  error,
  selectedTemplateId,
  onSelectTemplate,
  onClearSelection,
}: {
  templates: Template[]
  loading: boolean
  error: string | null
  selectedTemplateId: number | null
  onSelectTemplate: (templateId: number, styleKey: string) => void
  onClearSelection: () => void
}) {
  if (loading) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-card px-4 py-9 text-center text-sm text-muted-foreground">
        加载品牌模板中...
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-dashed border-destructive/40 bg-destructive/5 px-4 py-9 text-center text-sm text-destructive">
        {error}
      </div>
    )
  }

  if (templates.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-card px-4 py-9 text-center text-sm text-muted-foreground">
        还没有可用的品牌模板，先去品牌模板页创建一个。
      </div>
    )
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <button
        type="button"
        onClick={onClearSelection}
        className={cn(
          'rounded-xl border border-dashed bg-card p-3 text-left transition-all',
          selectedTemplateId === null
            ? 'border-primary ring-2 ring-primary/70'
            : 'border-border hover:border-border/80 hover:bg-accent/20',
        )}
      >
        <div className="text-sm font-semibold text-foreground">不使用品牌模板</div>
        <div className="mt-1 text-xs text-muted-foreground">本次不叠加品牌元素或模板字幕风格。</div>
      </button>
      {templates.map((template) => {
        const templateStyleKey = getTemplateCaptionStyleKey(template.config) ?? 'blue'
        const templateStyle = captionLibraryStyles.find((item) => item.key === templateStyleKey)
        const selected = selectedTemplateId === template.id

        return (
          <button
            key={template.id}
            type="button"
            onClick={() => onSelectTemplate(template.id, templateStyleKey)}
            className={cn(
              'rounded-xl border bg-card p-3 text-left transition-all',
              selected
                ? 'border-primary ring-2 ring-primary/70'
                : 'border-border hover:border-border/80 hover:bg-accent/20',
            )}
          >
            <div className="flex items-start gap-3">
              <img
                src={`${TEMPLATE_BASE}/${templateStyleKey}.jpg`}
                alt={template.templateName}
                className="h-14 w-20 rounded-md object-cover"
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-foreground">
                  {template.templateName}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {templateStyle?.title ?? templateStyleKey}
                </div>
                <div className="mt-2 text-[11px] text-muted-foreground">
                  片段结果会优先套用这个模板的字幕风格
                </div>
              </div>
            </div>
          </button>
        )
      })}
    </div>
  )
}

export function ClipConfigCanvas() {
  const navigate = useNavigate()
  const search = useSearch({ from: '/app/clip-config' })
  const src = search.src ?? ''
  const sourceTitle = search.title?.trim() || null

  const [videoGenre, setVideoGenre] = useState('Talking')
  const [selectedStyle, setSelectedStyle] = useState<string | null>(null)
  const [brandTemplates, setBrandTemplates] = useState<Template[]>([])
  const [brandTemplatesLoading, setBrandTemplatesLoading] = useState(false)
  const [brandTemplatesError, setBrandTemplatesError] = useState<string | null>(null)
  const [selectedBrandTemplateId, setSelectedBrandTemplateId] = useState<number | null>(null)
  const [styleDialogOpen, setStyleDialogOpen] = useState(false)
  const [pendingStyle, setPendingStyle] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'caption-styles' | 'brand-templates'>('caption-styles')
  const [language, setLanguage] = useState('zh')
  const [translateTo, setTranslateTo] = useState('none')
  const [orientation, setOrientation] = useState('portrait')
  const [resolution, setResolution] = useState('720')
  const [clipLength, setClipLength] = useState('<30s')
  const [aiMode, setAiMode] = useState<'auto' | 'custom'>('auto')
  const [customPrompt, setCustomPrompt] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [videoDuration, setVideoDuration] = useState<string | null>(null)
  const [videoDurationSec, setVideoDurationSec] = useState<number | null>(null)

  useEffect(() => {
    if (styleDialogOpen) {
      setPendingStyle(selectedStyle)
    }
  }, [selectedStyle, styleDialogOpen])

  useEffect(() => {
    let cancelled = false
    setBrandTemplatesLoading(true)
    setBrandTemplatesError(null)

    const params = new URLSearchParams({
      templateScope: 'brand',
      page: '1',
      pageSize: '50',
    })

    void appFetch<ApiResponse<PaginatedResponse<Template>>>(`/api/templates?${params.toString()}`)
      .then((res) => {
        if (!cancelled) {
          setBrandTemplates(res.data.items)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setBrandTemplatesError(err instanceof Error ? err.message : 'Failed to load brand templates')
        }
      })
      .finally(() => {
        if (!cancelled) {
          setBrandTemplatesLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [])

  const isUpload = src.startsWith('/uploads/')
  const mediaUrl = resolveMediaUrl(src)
  const videoTitle = sourceTitle ?? (isUpload ? extractFileName(src) : src.slice(0, 50))
  const selectedStyleObj = captionLibraryStyles.find((s) => s.key === selectedStyle)
  const selectedBrandTemplate =
    brandTemplates.find((template) => template.id === selectedBrandTemplateId) ?? null
  const selectedBrandTemplateStyle = getTemplateCaptionStyleKey(selectedBrandTemplate?.config)

  const handleCreateClips = async () => {
    if (!src) {
      setError('缺少视频来源')
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      const effectiveCaptionStyle = selectedBrandTemplateStyle ?? selectedStyle
      const res = await appFetch<ApiResponse<{ taskId: string; projectId: number; status: string }>>(
        '/api/clipping/analyze',
        {
          method: 'POST',
          headers: { 'X-Client-Request-Id': crypto.randomUUID() },
          body: JSON.stringify({
            src,
            language,
            clipLength,
            videoGenre,
            customPrompt: aiMode === 'custom' ? customPrompt : undefined,
            captionStyle: effectiveCaptionStyle ?? undefined,
            brandTemplateId: selectedBrandTemplateId ?? undefined,
            sourceTitle: videoTitle,
            orientation,
            resolution,
            translateTo,
          }),
        },
      )

      navigate({
        to: '/app/clip-processing',
        search: {
          taskId: res.data.taskId,
          src,
          title: videoTitle,
          style: effectiveCaptionStyle ?? undefined,
          language,
          orientation,
          resolution,
        },
      })

      generateAndUploadCover(res.data.projectId, src).catch((err) => {
        console.error('[cover] failed to upload first frame', err)
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create clipping task')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-background/95 px-5 backdrop-blur">
        <Link
          to="/app"
          className="flex items-center gap-1.5 text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="size-5" />
        </Link>
        <span className="text-sm font-semibold text-foreground">创建片段</span>
      </div>

      <div className="sticky top-14 z-20 mx-auto flex max-w-[724px] flex-wrap items-start justify-between gap-2.5 rounded-2xl border border-border bg-card px-3 py-3 shadow-md sm:gap-6 sm:px-5">
        <div className="flex w-full items-start gap-3 sm:max-w-[60%]">
          <div className="relative shrink-0">
            <div className="flex h-[65px] w-[107px] items-center justify-center overflow-hidden rounded-md bg-muted">
              {isUpload ? (
                <video
                  src={mediaUrl}
                  className="h-full w-full object-cover"
                  muted
                  playsInline
                  preload="auto"
                  crossOrigin="anonymous"
                  onLoadedMetadata={(e) => {
                    const v = e.currentTarget
                    if (v.duration && Number.isFinite(v.duration)) {
                      setVideoDuration(formatDuration(v.duration))
                      setVideoDurationSec(v.duration)
                    }
                  }}
                  onLoadedData={(e) => {
                    try {
                      e.currentTarget.currentTime = 0.1
                    } catch {
                      // ignore
                    }
                  }}
                />
              ) : (
                <Clapperboard className="size-6 text-muted-foreground" />
              )}
            </div>
            {videoDuration ? (
              <span className="absolute bottom-1 right-1 rounded bg-black/75 px-1 py-0.5 text-[10px] font-medium leading-none text-white">
                {videoDuration}
              </span>
            ) : null}
          </div>
          <div className="min-w-0 flex-1 pt-1">
            <div className="truncate text-sm font-medium text-foreground">{videoTitle}</div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              {videoDuration ? `时长 ${videoDuration}` : isUpload ? '已上传文件' : '视频链接'}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={handleCreateClips}
          disabled={submitting}
          className="inline-flex h-9 items-center gap-1.5 rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          {submitting ? <Loader2 className="size-4 animate-spin" /> : null}
          获取片段
        </button>
      </div>

      <div className="mx-auto max-w-[548px] space-y-[30px] px-5 py-10">
        <div>
          <label className="block text-sm font-medium leading-tight text-foreground">选择视频类型</label>
          <div className="mt-4 flex flex-wrap gap-1.5">
            {videoGenres.map((g) => (
              <PillButton
                key={g.value}
                active={videoGenre === g.value}
                onClick={() => setVideoGenre(g.value)}
              >
                {g.label}
              </PillButton>
            ))}
          </div>
        </div>

        <div>
          <div className="flex justify-between border-b border-border pb-2">
            <div className="flex gap-6">
              <button
                type="button"
                onClick={() => setActiveTab('caption-styles')}
                className={cn(
                  'whitespace-nowrap pb-1 text-sm font-medium transition-colors',
                  activeTab === 'caption-styles'
                    ? 'border-b-2 border-foreground text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                字幕样式
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('brand-templates')}
                className={cn(
                  'whitespace-nowrap pb-1 text-sm font-medium transition-colors',
                  activeTab === 'brand-templates'
                    ? 'border-b-2 border-foreground text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                品牌模板
              </button>
            </div>
          </div>

          {activeTab === 'caption-styles' ? (
            <>
              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedStyle(null)
                    setSelectedBrandTemplateId(null)
                  }}
                  className="group text-left"
                >
                  <div
                    className={cn(
                      'flex h-[62px] w-[100px] items-center justify-center rounded-lg border border-dashed bg-card text-xs font-medium text-muted-foreground transition-all',
                      selectedStyle === null
                        ? 'border-primary ring-2 ring-primary/80'
                        : 'border-border group-hover:border-border/80',
                    )}
                  >
                    不添加字幕
                  </div>
                  <div className="pt-1.5 text-center text-[11px] font-medium text-foreground">不选择</div>
                </button>
                {previewStyles.map((style) => (
                  <button
                    key={style.key}
                    type="button"
                    onClick={() => {
                      setSelectedStyle(style.key)
                      setSelectedBrandTemplateId(null)
                    }}
                    className="group text-left"
                  >
                    <div
                      className={cn(
                        'relative h-[62px] w-[100px] overflow-hidden rounded-lg border shadow-sm transition-all',
                        selectedStyle === style.key
                          ? 'border-primary ring-2 ring-primary/80'
                          : 'border-border group-hover:border-border/80',
                      )}
                    >
                      <video
                        loop
                        playsInline
                        muted
                        preload="none"
                        poster={`${TEMPLATE_BASE}/${style.key}.jpg`}
                        className="h-full w-full object-cover"
                        onMouseEnter={(e) => {
                          e.currentTarget.play().catch(() => {})
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.pause()
                        }}
                      >
                        <source src={`${TEMPLATE_BASE}/${style.key}.mp4`} type="video/mp4" />
                      </video>
                    </div>
                    <div className="pt-1.5 text-center text-[11px] font-medium text-foreground">
                      {style.title}
                    </div>
                  </button>
                ))}
              </div>

              {selectedStyleObj && !previewStyles.find((s) => s.key === selectedStyle) ? (
                <div className="mt-3 flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 shadow-sm">
                  <img
                    src={`${TEMPLATE_BASE}/${selectedStyle}.jpg`}
                    alt={selectedStyleObj.title}
                    className="size-9 rounded-md object-cover"
                  />
                  <div>
                    <div className="text-sm font-semibold text-foreground">{selectedStyleObj.title}</div>
                    <div className="text-xs text-muted-foreground">已选择</div>
                  </div>
                </div>
              ) : null}

              <div className="mt-4 flex justify-center">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStyleDialogOpen(true)}
                  className="h-9 rounded-full px-5 text-sm font-medium"
                >
                  更多样式
                </Button>
              </div>
            </>
          ) : (
            <BrandTemplatePicker
              templates={brandTemplates}
              loading={brandTemplatesLoading}
              error={brandTemplatesError}
              selectedTemplateId={selectedBrandTemplateId}
              onSelectTemplate={(templateId, styleKey) => {
                setSelectedBrandTemplateId(templateId)
                setSelectedStyle(styleKey)
              }}
              onClearSelection={() => {
                setSelectedBrandTemplateId(null)
                setSelectedStyle(null)
              }}
            />
          )}
        </div>

        <div className="flex items-start justify-between gap-4 rounded-xl border border-border bg-card p-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Type className="size-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">自动文本钩子</span>
              <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                即将上线
              </span>
            </div>
            <p className="mt-1.5 text-xs text-muted-foreground">
              自动在每个片段开头加入吸引注意的一行文字钩子，可在编辑器中修改。
            </p>
          </div>
        </div>

        <div className="flex items-start justify-between gap-4 rounded-xl border border-border bg-card p-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Scan className="size-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">人脸追踪</span>
              <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                即将上线
              </span>
            </div>
            <p className="mt-1.5 text-xs text-muted-foreground">
              自动保持人物居中，适合口播与解说类分片。
            </p>
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <SelectField label="语言" icon={Languages} value={language} onChange={setLanguage} options={languages} />
          <SelectField
            label="翻译为"
            icon={Languages}
            value={translateTo}
            onChange={setTranslateTo}
            options={[{ value: 'none', label: '不翻译' }, ...languages]}
          />
          <SelectField
            label="方向"
            icon={Monitor}
            value={orientation}
            onChange={setOrientation}
            options={orientations}
          />
          <SelectField
            label="分辨率"
            icon={Monitor}
            value={resolution}
            onChange={setResolution}
            options={resolutions}
          />
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Clock className="size-4 text-muted-foreground" />
            处理时间范围
          </label>
          <div className="mt-4 flex justify-between text-sm font-medium text-foreground">
            <span>00:00</span>
            <span>完整视频</span>
          </div>
          <div className="mt-2 h-1.5 w-full rounded-full bg-muted">
            <div className="h-1.5 w-full rounded-full bg-primary" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium leading-tight text-foreground">自动片段长度</label>
          <div className="mt-4 flex flex-wrap gap-1.5">
            {clipLengths.map((item) => {
              const disabled = videoDurationSec != null && item.maxSeconds > videoDurationSec
              return (
                <PillButton
                  key={item.value}
                  active={clipLength === item.value}
                  disabled={disabled}
                  onClick={() => setClipLength(item.value)}
                >
                  {item.label}
                </PillButton>
              )
            })}
          </div>
          {videoDurationSec != null && videoDurationSec < 30 ? (
            <p className="mt-2 text-xs text-muted-foreground">
              当前视频时长较短，建议直接处理完整视频。
            </p>
          ) : null}
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Wand2 className="size-4 text-muted-foreground" />
            AI 分析模式
          </label>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={() => setAiMode('auto')}
              className={cn(
                'flex-1 rounded-xl border p-4 text-left transition-colors',
                aiMode === 'auto' ? 'border-primary bg-primary/5' : 'border-border bg-card hover:bg-accent',
              )}
            >
              <div className="flex items-center gap-2">
                <Sparkles className="size-4 text-primary" />
                <span className="text-sm font-semibold text-foreground">自动智能</span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">系统自动分析并提取最精彩的片段。</p>
            </button>
            <button
              type="button"
              onClick={() => setAiMode('custom')}
              className={cn(
                'flex-1 rounded-xl border p-4 text-left transition-colors',
                aiMode === 'custom' ? 'border-primary bg-primary/5' : 'border-border bg-card hover:bg-accent',
              )}
            >
              <div className="flex items-center gap-2">
                <Wand2 className="size-4 text-primary" />
                <span className="text-sm font-semibold text-foreground">自定义提示词</span>
                <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                  Beta
                </span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">指定主题、节奏或平台偏好，让 AI 更精准取段。</p>
            </button>
          </div>

          {aiMode === 'custom' ? (
            <div className="mt-3">
              <textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder="例如：优先提取带冲突、观点明确、适合短视频传播的段落..."
                className="h-20 w-full resize-none rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          ) : null}
        </div>

        {error ? <p className="text-sm text-destructive">{error}</p> : null}

      </div>

      <Dialog open={styleDialogOpen} onOpenChange={setStyleDialogOpen}>
        <DialogContent className="w-[min(92vw,800px)] max-w-[800px] gap-0 overflow-hidden border-border bg-card p-0 shadow-2xl sm:rounded-2xl">
          <DialogHeader className="border-b border-border px-6 py-4 pr-14">
            <DialogTitle className="text-base font-semibold">选择字幕样式</DialogTitle>
          </DialogHeader>

          <ScrollArea className="h-[min(70vh,560px)] px-6 py-4">
            <div className="flex flex-wrap justify-center gap-3 pb-2">
              {captionLibraryStyles.map((style) => (
                <CaptionStyleLibraryCard
                  key={style.key}
                  style={style}
                  selected={pendingStyle === style.key}
                  onSelect={setPendingStyle}
                />
              ))}
            </div>
          </ScrollArea>

          <div className="flex items-center justify-end gap-3 border-t border-border bg-card px-6 py-3">
            <Button
              type="button"
              variant="ghost"
              className="h-9 rounded-full px-5 text-sm font-medium text-muted-foreground hover:text-foreground"
              onClick={() => setStyleDialogOpen(false)}
            >
              取消
            </Button>
            <Button
              type="button"
              className="h-9 rounded-full px-6 text-sm font-semibold"
              disabled={pendingStyle === null}
              onClick={() => {
                if (pendingStyle === null) return
                setSelectedStyle(pendingStyle)
                setSelectedBrandTemplateId(null)
                setStyleDialogOpen(false)
              }}
            >
              确认选择
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
